import type { Question } from '../../types';
import { HAB_MH_CONDITION, US_STATE_OPTIONS } from './shared-options';

// ---------------------------------------------------------------------------
// Auto / Fleet Supplement
// Source: Centered Partners MHMF intake — "Auto & Fleet" sheet.
// Driver list + vehicle schedule (golf carts, gators, and maintenance
// vehicles often need separate scheduled coverage — captured here too).
// ---------------------------------------------------------------------------

const AUTO_CONDITION = [
  { field: 'coverage_lines', operator: 'contains' as const, value: 'commercial_auto' },
  ...HAB_MH_CONDITION,
];

export const autoFleetQuestions: Question[] = [
  {
    id: 'af_vehicles',
    text: 'Vehicle / equipment schedule',
    subtext:
      'Include golf carts, gators, and on-site maintenance vehicles even if not road-registered — they often need separate scheduled coverage.',
    section: 'vehicles',
    group: 'auto_fleet',
    sort_order: 500,
    field_type: 'repeating_group',
    required: true,
    field_path: 'auto.vehicle_schedule',
    conditions: AUTO_CONDITION,
    repeating_group: {
      min_rows: 1,
      max_rows: 100,
      add_button_label: 'Add another vehicle',
      row_label: 'Vehicle',
      fields: [
        {
          id: 'af_v_year',
          text: 'Year',
          section: 'vehicles',
          sort_order: 10,
          field_type: 'number',
          placeholder: '2022',
          required: true,
          field_path: 'year',
          conditions: [],
        },
        {
          id: 'af_v_make_model',
          text: 'Make / model',
          section: 'vehicles',
          sort_order: 20,
          field_type: 'text',
          placeholder: 'Ford F-250',
          required: true,
          field_path: 'make_model',
          conditions: [],
        },
        {
          id: 'af_v_vin',
          text: 'VIN',
          section: 'vehicles',
          sort_order: 30,
          field_type: 'text',
          placeholder: '1FTBF2B61NEE12345',
          required: false,
          field_path: 'vin',
          conditions: [],
        },
        {
          id: 'af_v_garaging_city',
          text: 'Garaging city',
          section: 'vehicles',
          sort_order: 40,
          field_type: 'text',
          placeholder: 'St. Louis',
          required: true,
          field_path: 'garaging_city',
          conditions: [],
        },
        {
          id: 'af_v_garaging_state',
          text: 'Garaging state',
          section: 'vehicles',
          sort_order: 50,
          field_type: 'select',
          options: US_STATE_OPTIONS,
          required: true,
          field_path: 'garaging_state',
          conditions: [],
        },
        {
          id: 'af_v_type',
          text: 'GVW class / type',
          section: 'vehicles',
          sort_order: 60,
          field_type: 'select',
          options: [
            { label: 'Light truck / pickup (≤10k GVW)', value: 'light' },
            { label: 'Medium truck (10–26k GVW)', value: 'medium' },
            { label: 'Heavy truck (>26k GVW)', value: 'heavy' },
            { label: 'Passenger car / SUV / van', value: 'passenger' },
            { label: 'Golf cart / gator / utility', value: 'utility' },
            { label: 'Trailer', value: 'trailer' },
          ],
          required: true,
          field_path: 'gvw_type',
          conditions: [],
        },
        {
          id: 'af_v_cost',
          text: 'Cost new / ACV',
          section: 'vehicles',
          sort_order: 70,
          field_type: 'currency',
          placeholder: '48000',
          required: true,
          field_path: 'cost_new_acv',
          conditions: [],
        },
        {
          id: 'af_v_use',
          text: 'Primary use',
          section: 'vehicles',
          sort_order: 80,
          field_type: 'select',
          options: [
            { label: 'Owned — business use', value: 'owned' },
            { label: 'Pool vehicle', value: 'pool' },
            { label: 'Maintenance', value: 'maintenance' },
            { label: 'Golf cart / on-site only', value: 'golf_cart' },
          ],
          required: true,
          field_path: 'use',
          conditions: [],
        },
      ],
    },
    tags: ['auto', 'lob', 'repeating'],
  },
  {
    id: 'af_drivers',
    text: 'Driver list',
    subtext: 'Everyone who drives company vehicles. MVRs for the last 3 years will be requested.',
    section: 'vehicles',
    group: 'auto_fleet',
    sort_order: 510,
    field_type: 'repeating_group',
    required: true,
    field_path: 'auto.drivers',
    conditions: AUTO_CONDITION,
    repeating_group: {
      min_rows: 1,
      max_rows: 100,
      add_button_label: 'Add another driver',
      row_label: 'Driver',
      fields: [
        {
          id: 'af_d_name',
          text: 'Full name',
          section: 'vehicles',
          sort_order: 10,
          field_type: 'text',
          placeholder: 'Jane Smith',
          required: true,
          field_path: 'name',
          conditions: [],
        },
        {
          id: 'af_d_dob',
          text: 'Date of birth',
          section: 'vehicles',
          sort_order: 20,
          field_type: 'date',
          required: true,
          field_path: 'dob',
          conditions: [],
        },
        {
          id: 'af_d_license_state',
          text: 'License state',
          section: 'vehicles',
          sort_order: 30,
          field_type: 'select',
          options: US_STATE_OPTIONS,
          required: true,
          field_path: 'license_state',
          conditions: [],
        },
        {
          id: 'af_d_license_number',
          text: 'License number',
          section: 'vehicles',
          sort_order: 40,
          field_type: 'text',
          required: true,
          field_path: 'license_number',
          conditions: [],
        },
      ],
    },
    tags: ['auto', 'lob', 'repeating'],
  },
  {
    id: 'af_mvr_cadence',
    text: 'How often are motor vehicle records (MVRs) pulled?',
    section: 'vehicles',
    group: 'auto_fleet',
    sort_order: 520,
    field_type: 'select',
    options: [
      { label: 'Annually for all drivers', value: 'annual' },
      { label: 'At hire only', value: 'at_hire' },
      { label: 'Not pulled', value: 'none' },
    ],
    required: true,
    field_path: 'auto.mvr_cadence',
    conditions: AUTO_CONDITION,
    tags: ['auto', 'lob'],
  },
  {
    id: 'af_telematics',
    text: 'Is telematics / GPS monitoring installed in the fleet?',
    section: 'vehicles',
    group: 'auto_fleet',
    sort_order: 530,
    field_type: 'boolean',
    required: true,
    field_path: 'auto.telematics',
    conditions: AUTO_CONDITION,
    tags: ['auto', 'lob'],
  },
  {
    id: 'af_hnoa',
    text: 'Do employees use personal vehicles for business?',
    subtext: 'Hired & non-owned auto (HNOA) exposure.',
    section: 'vehicles',
    group: 'auto_fleet',
    sort_order: 540,
    field_type: 'boolean',
    required: true,
    field_path: 'auto.hnoa_exposure',
    conditions: AUTO_CONDITION,
    tags: ['auto', 'lob'],
  },
];
